import { EnemyId } from './enemies';
import { RosterHeroId } from './heroes';
import { Universe } from './mode';
import { TileKind } from './types';

export type WorldNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14;

export type LevelConfig = {
  id: string;
  world: WorldNumber;
  order: number;
  displayName: string;
  enemy: EnemyId;
  enemyMaxHp: number;
  enemyAttackIntervalMs: number;
  enemyAttackDamage: number;
  fillerKinds: TileKind[];
  isBoss?: boolean;
  bossSpecialEveryNAttacks?: number;
  bossSpecialDamage?: number;
  tutorialKey?: 'swap' | 'cascade';
  midUnlock?: RosterHeroId;
  portalTiles?: boolean;
  gravityTiles?: boolean;
  universe?: Universe;
};

export const WORLD_1_LEVELS: LevelConfig[] = [
  {
    id: 'w1-l1',
    world: 1,
    order: 1,
    displayName: 'Times Square Scramble',
    enemyMaxHp: 7,
    enemyAttackIntervalMs: 13000,
    enemyAttackDamage: 1,
    enemy: 'chitauri',
    fillerKinds: ['red', 'blue', 'green'],
    tutorialKey: 'swap',
  },
  {
    id: 'w1-l2',
    world: 1,
    order: 2,
    displayName: 'Grand Central Rush',
    enemyMaxHp: 9,
    enemyAttackIntervalMs: 12000,
    enemyAttackDamage: 1,
    enemy: 'chitauri',
    fillerKinds: ['red', 'blue', 'green'],
  },
  {
    id: 'w1-l3',
    world: 1,
    order: 3,
    displayName: 'Park Avenue Pileup',
    enemyMaxHp: 11,
    enemyAttackIntervalMs: 11000,
    enemyAttackDamage: 1,
    enemy: 'chitauri',
    fillerKinds: ['red', 'blue', 'yellow'],
  },
  {
    id: 'w1-l4',
    world: 1,
    order: 4,
    displayName: 'Brooklyn Bridge Brawl',
    enemyMaxHp: 13,
    enemyAttackIntervalMs: 10500,
    enemyAttackDamage: 1,
    enemy: 'chitauri',
    fillerKinds: ['blue', 'green', 'yellow'],
  },
  {
    id: 'w1-l5',
    world: 1,
    order: 5,
    displayName: 'Stark Tower Stand',
    enemyMaxHp: 18,
    enemyAttackIntervalMs: 10000,
    enemyAttackDamage: 1,
    enemy: 'chitauri-elite',
    fillerKinds: ['red', 'yellow', 'purple'],
  },
  {
    id: 'w1-l6',
    world: 1,
    order: 6,
    displayName: 'Midtown Meltdown',
    enemyMaxHp: 20,
    enemyAttackIntervalMs: 9500,
    enemyAttackDamage: 1,
    enemy: 'chitauri-captain',
    fillerKinds: ['blue', 'yellow', 'purple'],
  },
  {
    id: 'w1-boss',
    world: 1,
    order: 7,
    displayName: 'Thanos Arrives',
    enemy: 'thanos',
    enemyMaxHp: 28,
    enemyAttackIntervalMs: 9000,
    enemyAttackDamage: 1,
    fillerKinds: ['red', 'blue', 'green'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 3,
  },
];

export const WORLD_2_LEVELS: LevelConfig[] = [
  {
    id: 'w2-l1',
    world: 2,
    order: 1,
    displayName: 'Bifrost Landing',
    enemyMaxHp: 9,
    enemyAttackIntervalMs: 13000,
    enemyAttackDamage: 1,
    enemy: 'frost-giant',
    fillerKinds: ['blue', 'purple', 'green'],
  },
  {
    id: 'w2-l2',
    world: 2,
    order: 2,
    displayName: 'Palace Gates',
    enemyMaxHp: 11,
    enemyAttackIntervalMs: 12000,
    enemyAttackDamage: 1,
    enemy: 'frost-giant',
    fillerKinds: ['blue', 'yellow', 'green'],
  },
  {
    id: 'w2-l3',
    world: 2,
    order: 3,
    displayName: 'Frozen Stairs',
    enemyMaxHp: 13,
    enemyAttackIntervalMs: 11000,
    enemyAttackDamage: 1,
    enemy: 'frost-giant',
    fillerKinds: ['blue', 'green', 'purple'],
  },
  {
    id: 'w2-l4',
    world: 2,
    order: 4,
    displayName: 'Throne Hall',
    enemyMaxHp: 16,
    enemyAttackIntervalMs: 10500,
    enemyAttackDamage: 1,
    enemy: 'frost-giant-elite',
    fillerKinds: ['red', 'blue', 'purple'],
  },
  {
    id: 'w2-l5',
    world: 2,
    order: 5,
    displayName: 'Mirror Maze',
    enemyMaxHp: 18,
    enemyAttackIntervalMs: 10000,
    enemyAttackDamage: 1,
    enemy: 'frost-giant-elite',
    fillerKinds: ['green', 'yellow', 'purple'],
  },
  {
    id: 'w2-l6',
    world: 2,
    order: 6,
    displayName: 'Illusion Bridge',
    enemyMaxHp: 21,
    enemyAttackIntervalMs: 9500,
    enemyAttackDamage: 1,
    enemy: 'frost-giant-elite',
    fillerKinds: ['blue', 'yellow', 'red'],
  },
  {
    id: 'w2-boss',
    world: 2,
    order: 7,
    displayName: "Loki's Stand",
    enemy: 'loki',
    enemyMaxHp: 34,
    enemyAttackIntervalMs: 9000,
    enemyAttackDamage: 1,
    fillerKinds: ['green', 'yellow', 'purple'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 3,
  },
];

export const WORLD_3_LEVELS: LevelConfig[] = [
  {
    id: 'w3-l1',
    world: 3,
    order: 1,
    displayName: 'Lobby Defense',
    enemyMaxHp: 12,
    enemyAttackIntervalMs: 12500,
    enemyAttackDamage: 1,
    enemy: 'ultron-drone',
    fillerKinds: ['red', 'blue', 'yellow'],
  },
  {
    id: 'w3-l2',
    world: 3,
    order: 2,
    displayName: 'Server Floor',
    enemyMaxHp: 14,
    enemyAttackIntervalMs: 12000,
    enemyAttackDamage: 1,
    enemy: 'ultron-drone',
    fillerKinds: ['blue', 'purple', 'yellow'],
  },
  {
    id: 'w3-l3',
    world: 3,
    order: 3,
    displayName: 'Lab Level',
    enemyMaxHp: 16,
    enemyAttackIntervalMs: 11500,
    enemyAttackDamage: 1,
    enemy: 'ultron-drone',
    fillerKinds: ['red', 'purple', 'green'],
  },
  {
    id: 'w3-l4',
    world: 3,
    order: 4,
    displayName: 'Hangar Bay',
    enemyMaxHp: 19,
    enemyAttackIntervalMs: 11000,
    enemyAttackDamage: 1,
    enemy: 'ultron-brute',
    fillerKinds: ['red', 'blue', 'yellow'],
  },
  {
    id: 'w3-l5',
    world: 3,
    order: 5,
    displayName: 'Command Deck',
    enemyMaxHp: 22,
    enemyAttackIntervalMs: 10500,
    enemyAttackDamage: 1,
    enemy: 'ultron-brute',
    fillerKinds: ['blue', 'green', 'purple'],
  },
  {
    id: 'w3-l6',
    world: 3,
    order: 6,
    displayName: 'Rooftop Landing',
    enemyMaxHp: 25,
    enemyAttackIntervalMs: 10000,
    enemyAttackDamage: 1,
    enemy: 'ultron-brute',
    fillerKinds: ['red', 'yellow', 'purple'],
  },
  {
    id: 'w3-boss',
    world: 3,
    order: 7,
    displayName: "Ultron's Stand",
    enemy: 'ultron',
    enemyMaxHp: 40,
    enemyAttackIntervalMs: 9000,
    enemyAttackDamage: 1,
    fillerKinds: ['red', 'blue', 'purple'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 3,
  },
];

export const WORLD_4_LEVELS: LevelConfig[] = [
  {
    id: 'w4-l1',
    world: 4,
    order: 1,
    displayName: 'Border March',
    enemyMaxHp: 14,
    enemyAttackIntervalMs: 12000,
    enemyAttackDamage: 1,
    enemy: 'wakandan-merc',
    fillerKinds: ['red', 'green', 'yellow'],
  },
  {
    id: 'w4-l2',
    world: 4,
    order: 2,
    displayName: 'Savanna Patrol',
    enemyMaxHp: 16,
    enemyAttackIntervalMs: 11500,
    enemyAttackDamage: 1,
    enemy: 'wakandan-merc',
    fillerKinds: ['red', 'yellow', 'blue'],
  },
  {
    id: 'w4-l3',
    world: 4,
    order: 3,
    displayName: 'Tribal Gates',
    enemyMaxHp: 19,
    enemyAttackIntervalMs: 11000,
    enemyAttackDamage: 1,
    enemy: 'wakandan-merc-elite',
    fillerKinds: ['red', 'purple', 'green'],
  },
  {
    id: 'w4-l4',
    world: 4,
    order: 4,
    displayName: 'Royal Market',
    enemyMaxHp: 22,
    enemyAttackIntervalMs: 10500,
    enemyAttackDamage: 1,
    enemy: 'wakandan-merc-elite',
    fillerKinds: ['yellow', 'purple', 'blue'],
  },
  {
    id: 'w4-l5',
    world: 4,
    order: 5,
    displayName: 'Vibranium Mines',
    enemyMaxHp: 25,
    enemyAttackIntervalMs: 10000,
    enemyAttackDamage: 1,
    enemy: 'wakandan-merc-elite',
    fillerKinds: ['red', 'blue', 'green'],
  },
  {
    id: 'w4-l6',
    world: 4,
    order: 6,
    displayName: 'Klaw Strikes',
    enemyMaxHp: 30,
    enemyAttackIntervalMs: 9500,
    enemyAttackDamage: 1,
    enemy: 'klaw',
    fillerKinds: ['blue', 'yellow', 'purple'],
  },
  {
    id: 'w4-boss',
    world: 4,
    order: 7,
    displayName: "Killmonger's Throne",
    enemy: 'killmonger',
    enemyMaxHp: 42,
    enemyAttackIntervalMs: 9500,
    enemyAttackDamage: 1,
    fillerKinds: ['red', 'yellow', 'purple'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 3,
  },
];

export const WORLD_5_LEVELS: LevelConfig[] = [
  {
    id: 'w5-l1',
    world: 5,
    order: 1,
    displayName: 'Starforce Patrol',
    enemyMaxHp: 18,
    enemyAttackIntervalMs: 11000,
    enemyAttackDamage: 1,
    enemy: 'kree-soldier',
    fillerKinds: ['blue', 'purple', 'yellow'],
  },
  {
    id: 'w5-l2',
    world: 5,
    order: 2,
    displayName: 'Void Crossing',
    enemyMaxHp: 21,
    enemyAttackIntervalMs: 10500,
    enemyAttackDamage: 1,
    enemy: 'kree-soldier',
    fillerKinds: ['red', 'blue', 'purple'],
  },
  {
    id: 'w5-l3',
    world: 5,
    order: 3,
    displayName: 'Nebula Siege',
    enemyMaxHp: 24,
    enemyAttackIntervalMs: 10000,
    enemyAttackDamage: 1,
    enemy: 'kree-soldier',
    fillerKinds: ['yellow', 'purple', 'green'],
  },
  {
    id: 'w5-l4',
    world: 5,
    order: 4,
    displayName: 'Battle Barge',
    enemyMaxHp: 28,
    enemyAttackIntervalMs: 9800,
    enemyAttackDamage: 1,
    enemy: 'kree-warrior',
    fillerKinds: ['red', 'yellow', 'blue'],
  },
  {
    id: 'w5-l5',
    world: 5,
    order: 5,
    displayName: 'Kree Citadel',
    enemyMaxHp: 32,
    enemyAttackIntervalMs: 9500,
    enemyAttackDamage: 1,
    enemy: 'kree-warrior',
    fillerKinds: ['red', 'purple', 'green'],
  },
  {
    id: 'w5-l6',
    world: 5,
    order: 6,
    displayName: "Accuser's Challenge",
    enemyMaxHp: 36,
    enemyAttackIntervalMs: 9200,
    enemyAttackDamage: 1,
    enemy: 'kree-warrior',
    fillerKinds: ['blue', 'yellow', 'green'],
  },
  {
    id: 'w5-boss',
    world: 5,
    order: 7,
    displayName: 'Thanos Returns',
    enemy: 'thanos',
    enemyMaxHp: 50,
    enemyAttackIntervalMs: 9000,
    enemyAttackDamage: 1,
    fillerKinds: ['red', 'blue', 'yellow'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 4,
  },
];

export const WORLD_6_LEVELS: LevelConfig[] = [
  {
    id: 'w6-l1',
    world: 6,
    order: 1,
    displayName: 'Rift Entry',
    enemyMaxHp: 22,
    enemyAttackIntervalMs: 10500,
    enemyAttackDamage: 1,
    enemy: 'mindless-one',
    fillerKinds: ['purple', 'red', 'yellow'],
  },
  {
    id: 'w6-l2',
    world: 6,
    order: 2,
    displayName: 'Shattered Realm',
    enemyMaxHp: 26,
    enemyAttackIntervalMs: 10000,
    enemyAttackDamage: 1,
    enemy: 'mindless-one',
    fillerKinds: ['purple', 'blue', 'green'],
  },
  {
    id: 'w6-l3',
    world: 6,
    order: 3,
    displayName: 'Floating Citadel',
    enemyMaxHp: 30,
    enemyAttackIntervalMs: 9800,
    enemyAttackDamage: 1,
    enemy: 'mindless-one',
    fillerKinds: ['red', 'yellow', 'purple'],
  },
  {
    id: 'w6-l4',
    world: 6,
    order: 4,
    displayName: 'Echo Gate',
    enemyMaxHp: 34,
    enemyAttackIntervalMs: 9500,
    enemyAttackDamage: 1,
    enemy: 'mindless-one-elite',
    fillerKinds: ['purple', 'yellow', 'blue'],
  },
  {
    id: 'w6-l5',
    world: 6,
    order: 5,
    displayName: 'Fracture Spire',
    enemyMaxHp: 38,
    enemyAttackIntervalMs: 9200,
    enemyAttackDamage: 1,
    enemy: 'mindless-one-elite',
    fillerKinds: ['red', 'purple', 'green'],
  },
  {
    id: 'w6-l6',
    world: 6,
    order: 6,
    displayName: 'Eternal Abyss',
    enemyMaxHp: 42,
    enemyAttackIntervalMs: 9000,
    enemyAttackDamage: 1,
    enemy: 'mindless-one-elite',
    fillerKinds: ['purple', 'red', 'yellow'],
  },
  {
    id: 'w6-boss',
    world: 6,
    order: 7,
    displayName: "Dormammu's Domain",
    enemy: 'dormammu',
    enemyMaxHp: 54,
    enemyAttackIntervalMs: 8800,
    enemyAttackDamage: 1,
    fillerKinds: ['red', 'purple', 'yellow'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 4,
  },
];

export const WORLD_7_LEVELS: LevelConfig[] = [
  {
    id: 'w7-l1',
    world: 7,
    order: 1,
    displayName: 'Oscorp Ambush',
    enemyMaxHp: 24,
    enemyAttackIntervalMs: 10500,
    enemyAttackDamage: 1,
    enemy: 'oscorp-security',
    fillerKinds: ['red', 'blue', 'yellow'],
  },
  {
    id: 'w7-l2',
    world: 7,
    order: 2,
    displayName: 'Electro Blackout',
    enemyMaxHp: 30,
    enemyAttackIntervalMs: 10000,
    enemyAttackDamage: 1,
    enemy: 'electro',
    fillerKinds: ['blue', 'yellow', 'purple'],
  },
  {
    id: 'w7-l3',
    world: 7,
    order: 3,
    displayName: 'Sandman Sweep',
    enemyMaxHp: 34,
    enemyAttackIntervalMs: 9800,
    enemyAttackDamage: 1,
    enemy: 'sandman',
    fillerKinds: ['yellow', 'red', 'green'],
  },
  {
    id: 'w7-l4',
    world: 7,
    order: 4,
    displayName: "Doc Oc's Lab",
    enemyMaxHp: 38,
    enemyAttackIntervalMs: 9600,
    enemyAttackDamage: 1,
    enemy: 'doc-oc',
    fillerKinds: ['green', 'purple', 'blue'],
  },
  {
    id: 'w7-l5',
    world: 7,
    order: 5,
    displayName: "Goblin's Glider",
    enemyMaxHp: 42,
    enemyAttackIntervalMs: 9400,
    enemyAttackDamage: 1,
    enemy: 'green-goblin',
    fillerKinds: ['green', 'purple', 'red'],
    midUnlock: 'ghost-spider',
  },
  {
    id: 'w7-l6',
    world: 7,
    order: 6,
    displayName: 'Symbiote Swarm',
    enemyMaxHp: 34,
    enemyAttackIntervalMs: 9200,
    enemyAttackDamage: 1,
    enemy: 'oscorp-security',
    fillerKinds: ['red', 'blue', 'purple'],
  },
  {
    id: 'w7-boss',
    world: 7,
    order: 7,
    displayName: 'Venom Rampage',
    enemy: 'venom',
    enemyMaxHp: 56,
    enemyAttackIntervalMs: 8800,
    enemyAttackDamage: 1,
    fillerKinds: ['red', 'blue', 'purple'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 4,
  },
];

export const WORLD_8_LEVELS: LevelConfig[] = [
  {
    id: 'w8-l1',
    world: 8,
    order: 1,
    displayName: 'Danger Room',
    enemyMaxHp: 22,
    enemyAttackIntervalMs: 11000,
    enemyAttackDamage: 1,
    enemy: 'sentinel',
    fillerKinds: ['red', 'blue', 'yellow'],
  },
  {
    id: 'w8-l2',
    world: 8,
    order: 2,
    displayName: 'Sentinel Patrol',
    enemyMaxHp: 28,
    enemyAttackIntervalMs: 10500,
    enemyAttackDamage: 1,
    enemy: 'sentinel',
    fillerKinds: ['blue', 'purple', 'yellow'],
  },
  {
    id: 'w8-l3',
    world: 8,
    order: 3,
    displayName: 'Brotherhood Ambush',
    enemyMaxHp: 34,
    enemyAttackIntervalMs: 10000,
    enemyAttackDamage: 1,
    enemy: 'brotherhood-mutant',
    fillerKinds: ['red', 'purple', 'green'],
  },
  {
    id: 'w8-l4',
    world: 8,
    order: 4,
    displayName: 'X-Mansion Gates',
    enemyMaxHp: 40,
    enemyAttackIntervalMs: 9800,
    enemyAttackDamage: 1,
    enemy: 'brotherhood-mutant',
    fillerKinds: ['yellow', 'green', 'blue'],
  },
  {
    id: 'w8-l5',
    world: 8,
    order: 5,
    displayName: 'Mutant Trials',
    enemyMaxHp: 48,
    enemyAttackIntervalMs: 9500,
    enemyAttackDamage: 1,
    enemy: 'sentinel',
    fillerKinds: ['red', 'purple', 'blue'],
    portalTiles: true,
  },
  {
    id: 'w8-l6',
    world: 8,
    order: 6,
    displayName: 'Cerebro Breach',
    enemyMaxHp: 56,
    enemyAttackIntervalMs: 9200,
    enemyAttackDamage: 1,
    enemy: 'brotherhood-mutant',
    fillerKinds: ['purple', 'yellow', 'red'],
    portalTiles: true,
  },
  {
    id: 'w8-boss',
    world: 8,
    order: 7,
    displayName: 'Master of Magnetism',
    enemy: 'magneto',
    enemyMaxHp: 72,
    enemyAttackIntervalMs: 9000,
    enemyAttackDamage: 1,
    fillerKinds: ['red', 'purple', 'yellow'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 4,
    portalTiles: true,
  },
];

export const WORLD_9_LEVELS: LevelConfig[] = [
  {
    id: 'w9-l1',
    world: 9,
    order: 1,
    displayName: 'Xandar Outskirts',
    enemyMaxHp: 26,
    enemyAttackIntervalMs: 10800,
    enemyAttackDamage: 1,
    enemy: 'nova-corpsman',
    fillerKinds: ['blue', 'yellow', 'purple'],
  },
  {
    id: 'w9-l2',
    world: 9,
    order: 2,
    displayName: 'Landing Bay',
    enemyMaxHp: 32,
    enemyAttackIntervalMs: 10300,
    enemyAttackDamage: 1,
    enemy: 'nova-corpsman',
    fillerKinds: ['blue', 'red', 'yellow'],
  },
  {
    id: 'w9-l3',
    world: 9,
    order: 3,
    displayName: 'Ravager Raid',
    enemyMaxHp: 38,
    enemyAttackIntervalMs: 9900,
    enemyAttackDamage: 1,
    enemy: 'ravager',
    fillerKinds: ['red', 'yellow', 'green'],
  },
  {
    id: 'w9-l4',
    world: 9,
    order: 4,
    displayName: 'Kree Embassy',
    enemyMaxHp: 44,
    enemyAttackIntervalMs: 9700,
    enemyAttackDamage: 1,
    enemy: 'nova-corpsman',
    fillerKinds: ['blue', 'purple', 'green'],
  },
  {
    id: 'w9-l5',
    world: 9,
    order: 5,
    displayName: 'Black Hole Run',
    enemyMaxHp: 52,
    enemyAttackIntervalMs: 9400,
    enemyAttackDamage: 1,
    enemy: 'ravager',
    fillerKinds: ['purple', 'red', 'blue'],
    gravityTiles: true,
  },
  {
    id: 'w9-l6',
    world: 9,
    order: 6,
    displayName: 'Dark Aster Docking',
    enemyMaxHp: 60,
    enemyAttackIntervalMs: 9100,
    enemyAttackDamage: 1,
    enemy: 'ravager',
    fillerKinds: ['purple', 'yellow', 'red'],
    gravityTiles: true,
  },
  {
    id: 'w9-boss',
    world: 9,
    order: 7,
    displayName: 'Ronan the Accuser',
    enemy: 'ronan',
    enemyMaxHp: 76,
    enemyAttackIntervalMs: 8900,
    enemyAttackDamage: 1,
    fillerKinds: ['purple', 'red', 'blue'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 4,
    gravityTiles: true,
  },
];

export const WORLD_10_LEVELS: LevelConfig[] = [
  {
    id: 'w10-l1',
    world: 10,
    order: 1,
    displayName: 'Angel Grove Park',
    enemyMaxHp: 20,
    enemyAttackIntervalMs: 11000,
    enemyAttackDamage: 1,
    enemy: 'putty',
    fillerKinds: ['red', 'blue', 'yellow'],
    universe: 'powerrangers',
    tutorialKey: 'swap',
  },
  {
    id: 'w10-l2',
    world: 10,
    order: 2,
    displayName: 'Youth Center Attack',
    enemyMaxHp: 26,
    enemyAttackIntervalMs: 10600,
    enemyAttackDamage: 1,
    enemy: 'putty',
    fillerKinds: ['red', 'green', 'yellow'],
    universe: 'powerrangers',
  },
  {
    id: 'w10-l3',
    world: 10,
    order: 3,
    displayName: 'Junkyard Brawl',
    enemyMaxHp: 32,
    enemyAttackIntervalMs: 10200,
    enemyAttackDamage: 1,
    enemy: 'super-putty',
    fillerKinds: ['red', 'purple', 'blue'],
    universe: 'powerrangers',
  },
  {
    id: 'w10-l4',
    world: 10,
    order: 4,
    displayName: 'Downtown Standoff',
    enemyMaxHp: 38,
    enemyAttackIntervalMs: 9900,
    enemyAttackDamage: 1,
    enemy: 'super-putty',
    fillerKinds: ['red', 'yellow', 'green'],
    universe: 'powerrangers',
  },
  {
    id: 'w10-l5',
    world: 10,
    order: 5,
    displayName: 'Forest Ambush',
    enemyMaxHp: 44,
    enemyAttackIntervalMs: 9600,
    enemyAttackDamage: 1,
    enemy: 'super-putty',
    fillerKinds: ['green', 'yellow', 'purple'],
    universe: 'powerrangers',
    midUnlock: 'green-ranger',
  },
  {
    id: 'w10-l6',
    world: 10,
    order: 6,
    displayName: "Goldar's Challenge",
    enemyMaxHp: 52,
    enemyAttackIntervalMs: 9300,
    enemyAttackDamage: 1,
    enemy: 'goldar',
    fillerKinds: ['red', 'yellow', 'blue'],
    universe: 'powerrangers',
  },
  {
    id: 'w10-boss',
    world: 10,
    order: 7,
    displayName: "Rita's Wand",
    enemy: 'rita-repulsa',
    enemyMaxHp: 66,
    enemyAttackIntervalMs: 9000,
    enemyAttackDamage: 1,
    fillerKinds: ['red', 'purple', 'yellow'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 3,
    universe: 'powerrangers',
  },
];

export const WORLD_11_LEVELS: LevelConfig[] = [
  {
    id: 'w11-l1',
    world: 11,
    order: 1,
    displayName: 'Lunar Landing',
    enemyMaxHp: 34,
    enemyAttackIntervalMs: 10500,
    enemyAttackDamage: 1,
    enemy: 'tenga',
    fillerKinds: ['purple', 'blue', 'yellow'],
    universe: 'powerrangers',
  },
  {
    id: 'w11-l2',
    world: 11,
    order: 2,
    displayName: 'Crater Combat',
    enemyMaxHp: 40,
    enemyAttackIntervalMs: 10200,
    enemyAttackDamage: 1,
    enemy: 'tenga',
    fillerKinds: ['purple', 'red', 'green'],
    universe: 'powerrangers',
  },
  {
    id: 'w11-l3',
    world: 11,
    order: 3,
    displayName: 'Palace Steps',
    enemyMaxHp: 46,
    enemyAttackIntervalMs: 9900,
    enemyAttackDamage: 1,
    enemy: 'super-putty',
    fillerKinds: ['purple', 'blue', 'red'],
    universe: 'powerrangers',
  },
  {
    id: 'w11-l4',
    world: 11,
    order: 4,
    displayName: 'Throne Antechamber',
    enemyMaxHp: 54,
    enemyAttackIntervalMs: 9600,
    enemyAttackDamage: 1,
    enemy: 'tenga',
    fillerKinds: ['red', 'purple', 'yellow'],
    universe: 'powerrangers',
  },
  {
    id: 'w11-l5',
    world: 11,
    order: 5,
    displayName: 'Monster Lab',
    enemyMaxHp: 60,
    enemyAttackIntervalMs: 9300,
    enemyAttackDamage: 1,
    enemy: 'goldar',
    fillerKinds: ['green', 'purple', 'red'],
    universe: 'powerrangers',
    midUnlock: 'pink-ranger',
  },
  {
    id: 'w11-l6',
    world: 11,
    order: 6,
    displayName: 'Balcony Siege',
    enemyMaxHp: 68,
    enemyAttackIntervalMs: 9000,
    enemyAttackDamage: 1,
    enemy: 'goldar',
    fillerKinds: ['yellow', 'red', 'purple'],
    universe: 'powerrangers',
  },
  {
    id: 'w11-boss',
    world: 11,
    order: 7,
    displayName: "Zedd's Staff",
    enemy: 'lord-zedd',
    enemyMaxHp: 82,
    enemyAttackIntervalMs: 8700,
    enemyAttackDamage: 1,
    fillerKinds: ['red', 'purple', 'blue'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 4,
    universe: 'powerrangers',
  },
];

export const WORLD_12_LEVELS: LevelConfig[] = [
  {
    id: 'w12-l1',
    world: 12,
    order: 1,
    displayName: 'City Under Attack',
    enemyMaxHp: 46,
    enemyAttackIntervalMs: 10400,
    enemyAttackDamage: 1,
    enemy: 'tenga',
    fillerKinds: ['red', 'yellow', 'blue'],
    universe: 'powerrangers',
  },
  {
    id: 'w12-l2',
    world: 12,
    order: 2,
    displayName: 'Downtown Evacuation',
    enemyMaxHp: 52,
    enemyAttackIntervalMs: 10100,
    enemyAttackDamage: 1,
    enemy: 'super-putty',
    fillerKinds: ['red', 'green', 'purple'],
    universe: 'powerrangers',
  },
  {
    id: 'w12-l3',
    world: 12,
    order: 3,
    displayName: 'Megazord Descends',
    enemyMaxHp: 60,
    enemyAttackIntervalMs: 9800,
    enemyAttackDamage: 1,
    enemy: 'goldar',
    fillerKinds: ['red', 'yellow', 'purple'],
    universe: 'powerrangers',
  },
  {
    id: 'w12-l4',
    world: 12,
    order: 4,
    displayName: 'Ooze Eruption',
    enemyMaxHp: 68,
    enemyAttackIntervalMs: 9500,
    enemyAttackDamage: 1,
    enemy: 'tenga',
    fillerKinds: ['purple', 'green', 'yellow'],
    universe: 'powerrangers',
  },
  {
    id: 'w12-l5',
    world: 12,
    order: 5,
    displayName: 'Construction Site',
    enemyMaxHp: 74,
    enemyAttackIntervalMs: 9200,
    enemyAttackDamage: 1,
    enemy: 'super-putty',
    fillerKinds: ['red', 'purple', 'yellow'],
    universe: 'powerrangers',
  },
  {
    id: 'w12-l6',
    world: 12,
    order: 6,
    displayName: 'Angel Grove Skyline',
    enemyMaxHp: 82,
    enemyAttackIntervalMs: 8900,
    enemyAttackDamage: 1,
    enemy: 'goldar',
    fillerKinds: ['red', 'purple', 'blue'],
    universe: 'powerrangers',
  },
  {
    id: 'w12-boss',
    world: 12,
    order: 7,
    displayName: 'Ivan Ooze Rises',
    enemy: 'ivan-ooze',
    enemyMaxHp: 98,
    enemyAttackIntervalMs: 8500,
    enemyAttackDamage: 1,
    fillerKinds: ['red', 'purple', 'yellow'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 5,
    universe: 'powerrangers',
  },
];

export const WORLD_13_LEVELS: LevelConfig[] = [
  {
    id: 'w13-l1',
    world: 13,
    order: 1,
    displayName: "Alley Ambush",
    enemyMaxHp: 34,
    enemyAttackIntervalMs: 10600,
    enemyAttackDamage: 1,
    enemy: 'hand-ninja',
    fillerKinds: ['red', 'blue', 'purple'],
  },
  {
    id: 'w13-l2',
    world: 13,
    order: 2,
    displayName: "Rooftop Chase",
    enemyMaxHp: 40,
    enemyAttackIntervalMs: 10200,
    enemyAttackDamage: 1,
    enemy: 'hand-ninja',
    fillerKinds: ['red', 'yellow', 'green'],
  },
  {
    id: 'w13-l3',
    world: 13,
    order: 3,
    displayName: "Daily Bugle Lobby",
    enemyMaxHp: 46,
    enemyAttackIntervalMs: 9900,
    enemyAttackDamage: 1,
    enemy: 'hand-enforcer',
    fillerKinds: ['red', 'blue', 'green'],
  },
  {
    id: 'w13-l4',
    world: 13,
    order: 4,
    displayName: "Dockside Brawl",
    enemyMaxHp: 52,
    enemyAttackIntervalMs: 9600,
    enemyAttackDamage: 1,
    enemy: 'hand-enforcer',
    fillerKinds: ['purple', 'yellow', 'blue'],
  },
  {
    id: 'w13-l5',
    world: 13,
    order: 5,
    displayName: "Hell's Kitchen Rescue",
    enemyMaxHp: 58,
    enemyAttackIntervalMs: 9300,
    enemyAttackDamage: 1,
    enemy: 'hand-enforcer',
    fillerKinds: ['red', 'purple', 'yellow'],
    midUnlock: 'daredevil',
  },
  {
    id: 'w13-l6',
    world: 13,
    order: 6,
    displayName: "Bullseye Strikes",
    enemyMaxHp: 66,
    enemyAttackIntervalMs: 9000,
    enemyAttackDamage: 1,
    enemy: 'bullseye',
    fillerKinds: ['red', 'blue', 'yellow'],
  },
  {
    id: 'w13-boss',
    world: 13,
    order: 7,
    displayName: "Kingpin's Penthouse",
    enemy: 'kingpin',
    enemyMaxHp: 88,
    enemyAttackIntervalMs: 8700,
    enemyAttackDamage: 1,
    fillerKinds: ['red', 'purple', 'blue'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 4,
  },
];

export const WORLD_14_LEVELS: LevelConfig[] = [
  {
    id: 'w14-l1',
    world: 14,
    order: 1,
    displayName: "Deep Space Warning",
    enemyMaxHp: 42,
    enemyAttackIntervalMs: 10500,
    enemyAttackDamage: 1,
    enemy: 'cosmic-drone',
    fillerKinds: ['purple', 'blue', 'yellow'],
  },
  {
    id: 'w14-l2',
    world: 14,
    order: 2,
    displayName: "Asteroid Belt",
    enemyMaxHp: 48,
    enemyAttackIntervalMs: 10100,
    enemyAttackDamage: 1,
    enemy: 'cosmic-drone',
    fillerKinds: ['purple', 'red', 'green'],
  },
  {
    id: 'w14-l3',
    world: 14,
    order: 3,
    displayName: "Herald's Approach",
    enemyMaxHp: 56,
    enemyAttackIntervalMs: 9800,
    enemyAttackDamage: 1,
    enemy: 'cosmic-herald',
    fillerKinds: ['red', 'yellow', 'purple'],
  },
  {
    id: 'w14-l4',
    world: 14,
    order: 4,
    displayName: "Solar Fire Storm",
    enemyMaxHp: 64,
    enemyAttackIntervalMs: 9500,
    enemyAttackDamage: 1,
    enemy: 'cosmic-herald',
    fillerKinds: ['red', 'yellow', 'blue'],
  },
  {
    id: 'w14-l5',
    world: 14,
    order: 5,
    displayName: "Baxter Building Breach",
    enemyMaxHp: 70,
    enemyAttackIntervalMs: 9200,
    enemyAttackDamage: 1,
    enemy: 'cosmic-drone',
    fillerKinds: ['red', 'yellow', 'blue'],
    midUnlock: 'human-torch',
  },
  {
    id: 'w14-l6',
    world: 14,
    order: 6,
    displayName: "Silver Surfer Descends",
    enemyMaxHp: 78,
    enemyAttackIntervalMs: 8900,
    enemyAttackDamage: 1,
    enemy: 'silver-surfer',
    fillerKinds: ['blue', 'purple', 'green'],
  },
  {
    id: 'w14-boss',
    world: 14,
    order: 7,
    displayName: "Galactus Devourer",
    enemy: 'galactus',
    enemyMaxHp: 104,
    enemyAttackIntervalMs: 8500,
    enemyAttackDamage: 1,
    fillerKinds: ['red', 'purple', 'yellow'],
    isBoss: true,
    bossSpecialEveryNAttacks: 3,
    bossSpecialDamage: 5,
  },
];

export const WORLDS: Record<WorldNumber, LevelConfig[]> = {
  1: WORLD_1_LEVELS,
  2: WORLD_2_LEVELS,
  3: WORLD_3_LEVELS,
  4: WORLD_4_LEVELS,
  5: WORLD_5_LEVELS,
  6: WORLD_6_LEVELS,
  7: WORLD_7_LEVELS,
  8: WORLD_8_LEVELS,
  9: WORLD_9_LEVELS,
  10: WORLD_10_LEVELS,
  11: WORLD_11_LEVELS,
  12: WORLD_12_LEVELS,
  13: WORLD_13_LEVELS,
  14: WORLD_14_LEVELS,
};

export const ALL_WORLD_NUMBERS: WorldNumber[] = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
];

const WORLD_UNIVERSE: Record<WorldNumber, Universe> = {
  1: 'marvel',
  2: 'marvel',
  3: 'marvel',
  4: 'marvel',
  5: 'marvel',
  6: 'marvel',
  7: 'marvel',
  8: 'marvel',
  9: 'marvel',
  10: 'powerrangers',
  11: 'powerrangers',
  12: 'powerrangers',
  13: 'marvel',
  14: 'marvel',
};

export function universeForWorld(n: WorldNumber): Universe {
  return WORLD_UNIVERSE[n];
}

export function worldsForUniverse(u: Universe): WorldNumber[] {
  return ALL_WORLD_NUMBERS.filter((n) => universeForWorld(n) === u);
}

export function levelById(id: string): LevelConfig | undefined {
  for (const w of ALL_WORLD_NUMBERS) {
    const hit = WORLDS[w].find((l) => l.id === id);
    if (hit) return hit;
  }
  return undefined;
}

export function isLevelUnlocked(
  id: string,
  starsByLevelId: Record<string, number>,
): boolean {
  const level = levelById(id);
  if (!level) return false;
  const worldLevels = WORLDS[level.world];
  if (level.order === 1) return true;
  if (level.isBoss) {
    return worldLevels.filter((l) => !l.isBoss).every(
      (l) => (starsByLevelId[l.id] ?? 0) >= 1,
    );
  }
  const prev = worldLevels.find((l) => l.order === level.order - 1);
  if (!prev) return true;
  return (starsByLevelId[prev.id] ?? 0) >= 1;
}

export function isWorldUnlocked(
  _world: WorldNumber,
  _starsByLevelId: Record<string, number>,
): boolean {
  return true;
}

export function highestUnlockedWorld(starsByLevelId: Record<string, number>): WorldNumber {
  let highest: WorldNumber = 1;
  for (const w of ALL_WORLD_NUMBERS) {
    if (isWorldUnlocked(w, starsByLevelId) && WORLDS[w].length > 0) highest = w;
  }
  return highest;
}
